// ④ 支払明細 詳細（デザイン正本 screenA.jsx 準拠）
// MoneyForward 撤去。確定/保留/差戻し・承認依頼・確定の記録・A4支払通知書/売上明細表のPDF出力。
// 編集は控除（手入力控除・臨時控除）の金額補正に限定。売上は読み取り専用。

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import logo from "../assets/hsd-logo.png";
import { Ic } from "../components/Icons";
import { useStore, applyAdjustments, applyExtraDeductions, applyManualOverrides } from "../store";
import { yen } from "../lib/normalize";
import { noticeRecipient, displayInvoiceNo } from "../lib/calc/recipient";
import { INVOICE_DISCOUNT_NOTE, finalizeStatement } from "../lib/calc/common";
import { publishApprovals, fetchApprovalStatus } from "../lib/approvalApi";
import { fetchMonthRecord } from "../lib/archiveApi";
import { buildEmailByDriverId } from "../lib/notifyEmail";
import { filterAndSortStatements, filterLabel, hasActiveFilters, type StatementFilters } from "../lib/statementFilter";
import type { Carrier, ItemRow, ExtraDeduction, Statement, Adjustment, TaxRate } from "../types";

const CARRIER_LABEL: Record<Carrier, string> = { yamato: "ヤマト", sagawa: "佐川" };
const OTHER_OPTION = "その他";
const CONFIRMED_BY = "ICB"; // 確定者の表示名（事務所）

// 調整・臨時売上のプリセット（橋本光弘様の単価は本城さん 6/30 確定情報）
const ADJ_PRESETS: { name: string; unit: number | null; taxRate: TaxRate }[] = [
  { name: "全日", unit: 12000, taxRate: "10%" },
  { name: "半日", unit: 6000, taxRate: "10%" },
  { name: "時間超過", unit: 650, taxRate: "10%" },
  { name: "距離超過", unit: 20, taxRate: "10%" },
  { name: "高速代", unit: null, taxRate: "non" },
  { name: "代走調整", unit: null, taxRate: "10%" },
];

// 支払通知書の差出（発行元）情報。
const COMPANY = {
  name: "株式会社HS-deliverys",
  zip: "〒753-0056",
  addr: "山口県山口市湯田温泉3丁目7番5号",
  tel: "083-920-5210",
  invoiceNo: "T7120001236475", // 適格請求書発行事業者 登録番号
};

function nowStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function formatMonth(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-").map(Number);
  return `${y}年${m}月`;
}

// 売上を品目ごとに集約（A4通知書用）
function summarizeSales(stmt: Statement): { name: string; qty: number; amount: number; carrier?: Carrier; detail?: string; taxRate?: string }[] {
  const map = new Map<string, { name: string; qty: number; amount: number; carrier?: Carrier; detail?: string; taxRate?: string }>();
  for (const it of stmt.items) {
    if (it.category !== "sales") continue;
    // detail もキーに含める（品目詳細が異なる調整行は別の明細行として通知書に出す）
    // 税率もキーに含める（適格請求書は税率ごとに区分して記載する必要があるため）
    const key = `${it.carrier ?? ""}__${it.name}__${it.detail ?? ""}__${it.taxRate}`;
    const cur = map.get(key) || { name: it.name, qty: 0, amount: 0, carrier: it.carrier, detail: it.detail, taxRate: it.taxRate };
    cur.qty += it.quantity;
    cur.amount += it.amount;
    map.set(key, cur);
  }
  return [...map.values()];
}

export function StatementDetailScreen({
  driverId,
  onNav,
  onSelectDriver,
  filters,
}: {
  driverId: string;
  onNav: (s: string) => void;
  onSelectDriver?: (driverId: string) => void;
  filters?: StatementFilters;
}) {
  const { displayStatements, isArchive, monthLocked } = useStore();
  // 一覧と同じ並び（差引支給額の降順）＋一覧と同じ絞り込みで前/次を決める（本城さん要望 2026-08-24）。
  // ただし現在表示中の人が絞り込みに掛からない場合（承認状況から開いた等）は全員リストへフォールバック。
  const orderedAll = useMemo(
    () => [...displayStatements].sort((a, b) => b.netTotal - a.netTotal),
    [displayStatements]
  );
  const orderedFiltered = useMemo(
    () => (filters && hasActiveFilters(filters) ? filterAndSortStatements(displayStatements, filters) : orderedAll),
    [displayStatements, filters, orderedAll]
  );
  const filterApplied =
    orderedFiltered !== orderedAll && orderedFiltered.some((s) => s.driverId === driverId);
  const ordered = filterApplied ? orderedFiltered : orderedAll;
  const idx = ordered.findIndex((s) => s.driverId === driverId);
  const stmt = idx >= 0 ? ordered[idx] : displayStatements.find((s) => s.driverId === driverId);

  if (!stmt) {
    return (
      <div className="content">
        <div className="page-title">明細が見つかりません</div>
        <button className="btn" onClick={() => onNav("list")}>一覧に戻る</button>
      </div>
    );
  }

  const total = ordered.length;
  const position = idx >= 0 ? idx + 1 : 1;
  const goPrev = () => onSelectDriver?.(ordered[(idx - 1 + total) % total].driverId);
  const goNext = () => onSelectDriver?.(ordered[(idx + 1) % total].driverId);

  // key で driverId が変わるたびに内部 state（編集中の入力）をリセット
  return (
    <DetailBody
      key={stmt.driverId}
      stmt={stmt}
      position={position}
      total={total}
      navFilterLabel={filterApplied && filters ? filterLabel(filters) : ""}
      onPrev={goPrev}
      onNext={goNext}
      hasNav={!!onSelectDriver && total > 1}
      readOnly={isArchive}
      locked={monthLocked}
      onNav={onNav}
    />
  );
}

type Toast = { id: string; text: string; kind: "ok" | "warn" };

function DetailBody({
  stmt: stmtProp,
  position,
  total,
  navFilterLabel,
  onPrev,
  onNext,
  hasNav,
  readOnly,
  locked,
  onNav,
}: {
  stmt: Statement;
  position: number;
  total: number;
  navFilterLabel?: string;
  onPrev: () => void;
  onNext: () => void;
  hasNav: boolean;
  readOnly: boolean;
  // 確定した月のロック（本城さん要望 2026-09-01）。読み取り専用（過去月）とは分ける：
  // ロック中でも承認用の発行・PDF出力はできるが、金額と確定状態は触れない
  locked: boolean;
  onNav: (s: string) => void;
}) {
  const { state, dispatch, archivedRecord, correctArchived, archiveBusy, viewMonth } = useStore();

  // ===== 過去月の「この人だけ訂正」（本城さん要望 2026-10-06） =====
  // 確定済みの過去月を開いているとき、この人の明細だけを手元のコピー（draft）で直し、
  // 「訂正を保存」でサーバの保存データのうちこの1通だけを差し替える。
  // 作業中の月（state.statements）には一切 dispatch しない＝作業中の月も他の人も動かない。
  // この明細の月。過去月は開いている保存データの月、作業中は作業中の月（targetMonth を持たない古い明細でも取り違えない）
  const stmtMonth = readOnly ? (archivedRecord?.month ?? stmtProp.targetMonth) : state.targetMonth;
  const canCorrect = readOnly && archivedRecord != null && archivedRecord.status === "finalized" && archivedRecord.month !== state.targetMonth && (!stmtProp.targetMonth || stmtProp.targetMonth === archivedRecord.month);
  const [correcting, setCorrecting] = useState(false);
  const [draft, setDraft] = useState<Statement | null>(null);
  const [correctReason, setCorrectReason] = useState("");
  const stmt = correcting && draft ? draft : stmtProp;
  const draftChanged = correcting && draft != null && JSON.stringify(draft) !== JSON.stringify(stmtProp);

  // 編集できない条件（過去月 or 確定済みでロック中）。確定・保留・差戻しと手動明細の削除はこちらで止める
  const noEdit = readOnly || locked;
  // 控除・調整の金額を直せるか（通常の編集、または過去月の1人訂正中）
  const canEditAmounts = correcting || !noEdit;
  // 金額の変更を「作業中の月へ dispatch」するか「1人訂正の手元コピーに反映」するかを振り分ける
  const editStatement = (apply: (s: Statement) => Statement, action: Parameters<typeof dispatch>[0]) => {
    if (correcting) setDraft((d) => apply(d ?? stmtProp));
    else dispatch(action);
  };
  const driver = state.drivers.find((d) => d.id === stmt.driverId);

  const carriers = stmt.carriers ?? [stmt.carrier];
  const isMultiCarrier = carriers.length > 1;
  const masterMissing = stmt.warnings.some((w) => w.includes("マスター") && w.includes("未登録"));

  // 売上（日次）
  const dailyItems = useMemo(() => sortedSalesItems(stmt), [stmt.items]);
  const warnCount = dailyItems.filter((r) => (r.unitPrice ?? 0) === 0 && r.quantity > 0).length;

  // 控除表示
  const deductItems = stmt.items.filter((i) => i.category === "deduct" && i.note !== "__extra__");
  const manualItems = stmt.items.filter((i) => i.category === "manual");
  const savedExtras = stmt.extraDeductions || [];

  // ===== 控除編集（手入力控除 + 臨時控除） =====
  const presetNames = useMemo(() => state.deductionMaster.map((d) => d.name), [state.deductionMaster]);
  const presetOptions = useMemo(() => [...presetNames, OTHER_OPTION], [presetNames]);
  const defaultAmountByName = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of state.deductionMaster) if (d.defaultAmount != null) m.set(d.name, d.defaultAmount);
    return m;
  }, [state.deductionMaster]);

  const hasGasolineSlot = manualItems.some((i) => i.name === "ガソリンカード利用料");
  const shidoSlotCount = manualItems.filter((i) => i.name === "指導料").length;
  const overrides = stmt.manualOverrides || {};
  const [gasolineInput, setGasolineInput] = useState<string>(overrides.gasoline != null ? String(overrides.gasoline) : "");
  const [shidoInput, setShidoInput] = useState<string>(overrides.shido != null ? String(overrides.shido) : "");
  const [editOpen, setEditOpen] = useState(false);

  const applyOverrides = () => {
    const gasoline = gasolineInput.trim() === "" ? undefined : Number(gasolineInput);
    const shido = shidoInput.trim() === "" ? undefined : Number(shidoInput);
    if (gasoline != null && (!Number.isFinite(gasoline) || gasoline < 0)) { alert("ガソリン代は0以上の数字を入力してください"); return; }
    if (shido != null && (!Number.isFinite(shido) || shido < 0)) { alert("指導料単価は0以上の数字を入力してください"); return; }
    editStatement((s) => applyManualOverrides(s, { gasoline, shido }), { type: "SET_MANUAL_OVERRIDES", driverId: stmt.driverId, overrides: { gasoline, shido } });
    pushToast(correcting ? "手入力控除を反映しました（「訂正を保存」で保存されます）" : "手入力控除を保存しました");
  };
  const hasUnsavedManualEdit =
    (gasolineInput.trim() === "" ? undefined : Number(gasolineInput)) !== overrides.gasoline ||
    (shidoInput.trim() === "" ? undefined : Number(shidoInput)) !== overrides.shido;

  type ExtraRow = { id: string; preset: string; customName: string; amount: string; taxRate: TaxRate; detail: string };
  const toEditable = (eds: ExtraDeduction[] | undefined): ExtraRow[] =>
    (eds || []).map((e) => {
      const isPreset = presetNames.includes(e.name);
      return { id: e.id, preset: isPreset ? e.name : OTHER_OPTION, customName: isPreset ? "" : e.name, amount: String(e.amount), taxRate: e.taxRate ?? "non", detail: e.detail ?? "" };
    });
  const [extras, setExtras] = useState<ExtraRow[]>(() => toEditable(stmt.extraDeductions));
  const addExtra = () =>
    setExtras((rows) => [...rows, { id: `ex-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, preset: presetNames[0] ?? OTHER_OPTION, customName: "", amount: "", taxRate: "non", detail: "" }]);
  const updateExtra = (id: string, patch: Partial<ExtraRow>) => setExtras((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const onPresetChange = (id: string, preset: string) =>
    setExtras((rows) => rows.map((r) => {
      if (r.id !== id) return r;
      const def = defaultAmountByName.get(preset);
      const amount = r.amount.trim() === "" && def != null ? String(def) : r.amount;
      return { ...r, preset, amount };
    }));
  const removeExtra = (id: string) => setExtras((rows) => rows.filter((r) => r.id !== id));
  const toSavable = (rows: ExtraRow[]): { ok: true; value: ExtraDeduction[] } | { ok: false; error: string } => {
    const out: ExtraDeduction[] = [];
    for (const r of rows) {
      const name = r.preset === OTHER_OPTION ? r.customName.trim() : r.preset;
      const amt = Number(r.amount);
      if (r.amount.trim() === "") continue;
      if (!name) return { ok: false, error: "控除名を選んでください（その他のときは名称を入力）" };
      if (!Number.isFinite(amt) || amt <= 0) return { ok: false, error: `「${name}」の金額は1以上の数字で入力してください` };
      // 備考は任意。空なら持たない（旧データと同じ形を保つ）
      const detail = r.detail.trim();
      out.push({ id: r.id, name, amount: amt, taxRate: r.taxRate, ...(detail ? { detail } : {}) });
    }
    return { ok: true, value: out };
  };
  const currentDraft = toSavable(extras);
  const draftJson = currentDraft.ok ? JSON.stringify(currentDraft.value.map((e) => ({ n: e.name, a: e.amount, t: e.taxRate ?? "non", d: e.detail ?? "" }))) : "INVALID";
  const savedJson = JSON.stringify(savedExtras.map((e) => ({ n: e.name, a: e.amount, t: e.taxRate ?? "non", d: e.detail ?? "" })));
  const hasUnsavedExtras = draftJson !== savedJson;
  const applyExtras = () => {
    const result = toSavable(extras);
    if (!result.ok) { alert(result.error); return; }
    editStatement((s) => applyExtraDeductions(s, result.value), { type: "SET_EXTRA_DEDUCTIONS", driverId: stmt.driverId, deductions: result.value });
    pushToast(correcting ? "臨時控除を反映しました（「訂正を保存」で保存されます）" : "臨時控除を保存しました");
  };

  // ===== 調整・臨時売上（橋本様の全日/半日等・代走調整。売上側の手入力行） =====
  const isManual = !readOnly && state.manualDriverIds.includes(stmt.driverId);
  type AdjRow = { id: string; name: string; unitPrice: string; quantity: string; taxRate: TaxRate; detail: string };
  const toAdjEditable = (adjs: Adjustment[] | undefined): AdjRow[] =>
    (adjs || []).map((a) => ({ id: a.id, name: a.name, unitPrice: String(a.unitPrice), quantity: String(a.quantity), taxRate: a.taxRate, detail: a.detail ?? "" }));
  const [adjRows, setAdjRows] = useState<AdjRow[]>(() => toAdjEditable(stmt.adjustments));
  const addAdj = (preset?: (typeof ADJ_PRESETS)[number]) =>
    setAdjRows((rows) => [
      ...rows,
      {
        id: `adj-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: preset?.name ?? "",
        unitPrice: preset?.unit != null ? String(preset.unit) : "",
        quantity: "",
        taxRate: preset?.taxRate ?? "10%",
        detail: "",
      },
    ]);
  const updateAdj = (id: string, patch: Partial<AdjRow>) => setAdjRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeAdj = (id: string) => setAdjRows((rows) => rows.filter((r) => r.id !== id));
  const adjToSavable = (rows: AdjRow[]): { ok: true; value: Adjustment[] } | { ok: false; error: string } => {
    const out: Adjustment[] = [];
    for (const r of rows) {
      if (r.name.trim() === "" && r.unitPrice.trim() === "" && r.quantity.trim() === "") continue;
      const unit = Number(r.unitPrice);
      const qty = Number(r.quantity);
      if (!r.name.trim()) return { ok: false, error: "調整の名称を入力してください" };
      if (!Number.isFinite(unit)) return { ok: false, error: `「${r.name}」の単価は数字で入力してください` };
      if (!Number.isFinite(qty) || qty === 0) return { ok: false, error: `「${r.name}」の数量は0以外の数字で入力してください` };
      out.push({ id: r.id, name: r.name.trim(), unitPrice: unit, quantity: qty, taxRate: r.taxRate, detail: r.detail.trim() || undefined });
    }
    return { ok: true, value: out };
  };
  const adjDraft = adjToSavable(adjRows);
  const adjDraftJson = adjDraft.ok ? JSON.stringify(adjDraft.value.map((a) => ({ n: a.name, u: a.unitPrice, q: a.quantity, t: a.taxRate, d: a.detail ?? "" }))) : "INVALID";
  const adjSavedJson = JSON.stringify((stmt.adjustments || []).map((a) => ({ n: a.name, u: a.unitPrice, q: a.quantity, t: a.taxRate, d: a.detail ?? "" })));
  const hasUnsavedAdj = adjDraftJson !== adjSavedJson;
  const applyAdj = () => {
    const result = adjToSavable(adjRows);
    if (!result.ok) { alert(result.error); return; }
    editStatement((s) => applyAdjustments(s, result.value), { type: "SET_ADJUSTMENTS", driverId: stmt.driverId, adjustments: result.value });
    pushToast(correcting ? "調整・臨時売上を反映しました（「訂正を保存」で保存されます）" : "調整・臨時売上を保存しました");
  };
  const removeManual = () => {
    if (!window.confirm(`${stmt.driverName} 様の手動作成した通知書を削除します。よろしいですか？`)) return;
    dispatch({ type: "REMOVE_MANUAL_STATEMENT", driverId: stmt.driverId });
    onNav("list");
  };

  // ===== 通常の控除（携帯・車両リース料など、マスターから付いた行）の金額を直す =====
  // 1人訂正のときだけ使う。過去月は再計算しない（生CSVを持っていない）ため、確定時の行の金額をそのまま書き換える。
  const regularDeducts = stmt.items
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => it.category === "deduct" && it.note !== "__extra__" && it.note !== INVOICE_DISCOUNT_NOTE);
  const [regularInputs, setRegularInputs] = useState<Record<number, string>>({});
  const regularValue = (i: number, it: ItemRow) => regularInputs[i] ?? String(Math.abs(it.amount));
  const hasUnsavedRegular = regularDeducts.some(({ it, i }) => regularInputs[i] != null && Number(regularInputs[i]) !== Math.abs(it.amount));
  const applyRegular = () => {
    for (const { it, i } of regularDeducts) {
      const v = regularValue(i, it);
      const n = Number(v);
      if (v.trim() === "" || !Number.isFinite(n) || n < 0 || !Number.isInteger(n)) { alert(`「${it.name}」の金額は0以上の整数で入力してください（0にするとこの行を消します）`); return; }
    }
    setDraft((d) => {
      const base = d ?? stmtProp;
      const items: ItemRow[] = [];
      base.items.forEach((it, i) => {
        const target = regularDeducts.find((r) => r.i === i);
        if (!target || regularInputs[i] == null) { items.push(it); return; }
        const n = Number(regularInputs[i]);
        if (n === 0) return; // 0円＝この控除は無かった
        items.push({ ...it, quantity: 1, unitPrice: -n, amount: -n });
      });
      return finalizeStatement({ ...base, items });
    });
    setRegularInputs({});
    pushToast("通常の控除を反映しました（「訂正を保存」で保存されます）");
  };

  const resetEditors = () => {
    setGasolineInput(stmtProp.manualOverrides?.gasoline != null ? String(stmtProp.manualOverrides.gasoline) : "");
    setShidoInput(stmtProp.manualOverrides?.shido != null ? String(stmtProp.manualOverrides.shido) : "");
    setExtras(toEditable(stmtProp.extraDeductions));
    setAdjRows(toAdjEditable(stmtProp.adjustments));
    setRegularInputs({});
  };
  const startCorrection = () => {
    resetEditors();
    setDraft(stmtProp);
    setCorrectReason("");
    setCorrecting(true);
    setEditOpen(true);
  };
  const cancelCorrection = () => {
    if (draftChanged && !window.confirm("訂正した内容を保存せずに破棄します。よろしいですか？")) return;
    setCorrecting(false);
    setDraft(null);
    setEditOpen(false);
    resetEditors();
  };
  const unappliedInputs = hasUnsavedManualEdit || hasUnsavedExtras || hasUnsavedAdj || hasUnsavedRegular;
  const [justCorrected, setJustCorrected] = useState(false);
  const saveCorrection = async () => {
    if (!draft || !archivedRecord) return;
    if (unappliedInputs) { alert("「適用して保存」を押していない入力があります。反映するか、入力を元に戻してから保存してください。"); return; }
    if (!draftChanged) { alert("訂正前と内容が変わっていません。"); return; }
    const month = formatMonth(archivedRecord.month);
    if (!window.confirm(
      `${month}の ${stmtProp.driverName} 様の明細だけを訂正して保存します。\n\n` +
      `差引支給額：¥${yen(stmtProp.netTotal)} → ¥${yen(draft.netTotal)}\n\n` +
      `・他の方の${month}分と、いま作業中の月には影響しません\n` +
      `・訂正した人と日時、前後の金額が履歴に残ります\n` +
      `・本人へ訂正後の通知書を届けるには、保存後に「承認依頼を送り直す」を押してください\n\n続けますか？`
    )) return;
    const r = await correctArchived(draft, correctReason);
    if (r.conflict) {
      // 最新の保存データを読み直したので、古い内容を元にした訂正はいったん破棄してやり直してもらう
      setCorrecting(false);
      setDraft(null);
      setEditOpen(false);
      resetEditors();
      alert(`${r.error || "この月の保存データは、画面を開いた後に別の方が更新していました。"}\n\n訂正は保存していません。最新の保存データを読み込み直しました。`);
      return;
    }
    if (!r.ok) { pushToast(`訂正を保存できませんでした: ${r.error || "不明なエラー"}`, "warn"); return; }
    setCorrecting(false);
    setDraft(null);
    setEditOpen(false);
    setRegularInputs({});
    setJustCorrected(true);
    pushToast(`${stmtProp.driverName} 様の${month}分を訂正して保存しました`);
  };
  const corrections = (archivedRecord?.corrections ?? []).filter((c) => c.driverId === stmtProp.driverId);
  const goList = () => {
    if (draftChanged && !window.confirm("訂正した内容がまだ保存されていません。保存せずに一覧に戻りますか？")) return;
    onNav("list");
  };

  // ===== トースト =====
  const [toasts, setToasts] = useState<Toast[]>([]);
  function pushToast(text: string, kind: "ok" | "warn" = "ok") {
    const id = Math.random().toString(36).slice(2);
    setToasts((ts) => [...ts, { id, text, kind }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), 3200);
  }

  // ===== ステータス操作 =====
  const setStatus = (status: Statement["status"], label: string) => {
    const next: Statement = { ...stmt, status };
    if (status === "confirmed") { next.confirmedAt = nowStamp(); next.confirmedBy = CONFIRMED_BY; }
    if (status === "draft" || status === "hold") { next.confirmedAt = null; next.confirmedBy = null; }
    dispatch({ type: "UPDATE_STATEMENT", stmt: next });
    pushToast(`明細を「${label}」にしました`);
  };

  // ===== 承認依頼を送る（単票発行） =====
  const [publishBusy, setPublishBusy] = useState(false);
  const [approvedAt, setApprovedAt] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await fetchApprovalStatus(stmtMonth);
      if (!alive || !r.ok) return;
      const a = r.items.find((i) => i.driverId === stmt.driverId);
      if (a) {
        setRequestSent(true);
        setApprovedAt(a.approvedAt ? new Date(a.approvedAt).toLocaleString("ja-JP", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : null);
      }
    })();
    return () => { alive = false; };
  }, [stmtMonth, stmtProp.driverId]);

  const sendApproval = async () => {
    if (stmt.status !== "confirmed") { pushToast("先に明細を「確定」してから承認依頼を送ってください", "warn"); return; }
    if (correcting) { pushToast("先に「訂正を保存」してから送り直してください", "warn"); return; }
    if (readOnly && !window.confirm(
      `${formatMonth(stmtMonth)}分の承認依頼を、いま保存されている内容で ${stmtProp.driverName} 様へ送り直します。\n\n` +
      `・承認済みでも「未承認」に戻り、もう一度承認をお願いすることになります\n` +
      `・メールアドレスが登録されていれば、本人へメールが届きます\n\n続けますか？`
    )) return;
    setPublishBusy(true);
    try {
      // グループ宛通知書（driver がマスタに無い合成ID）もグループ設定の宛先へ送れるようにする
      const emailByDriverId = buildEmailByDriverId(state.drivers, state.groupRules);
      let target: Statement = stmtProp;
      if (readOnly) {
        // 過去月は送る直前に保存データを読み直す。画面を開いた後に別の方が訂正していたら、
        // 古い金額で承認をお願いしないよう、画面を最新にしてから押し直してもらう（Codex P1）
        const fresh = await fetchMonthRecord(stmtMonth);
        const freshStmt = fresh.ok ? fresh.record?.statements.find((s) => s.driverId === stmtProp.driverId) : undefined;
        if (!fresh.record || !freshStmt) { pushToast(`保存データを読み込めませんでした: ${fresh.error || "この方の明細が見つかりません"}`, "warn"); return; }
        if (fresh.record.savedAt !== archivedRecord?.savedAt) {
          await viewMonth(stmtMonth);
          alert("この月の保存データが、画面を開いた後に更新されていました。最新の内容を表示したので、金額を確認してからもう一度「承認依頼を送り直す」を押してください。");
          return;
        }
        target = freshStmt;
      }
      const r = await publishApprovals(stmtMonth, [target], emailByDriverId);
      if (r.ok && r.published && r.published.length > 0) {
        setRequestSent(true);
        // 再発行するとサーバ側は承認日時を消して「未承認」に戻す。画面の承認済み表示も合わせて消す（Codex P2）
        setApprovedAt(null);
        const mailed = r.mail?.sent ? "・メールを送信しました" : r.mail && r.mail.configured && r.mail.failed ? "（メール送信に失敗）" : "";
        pushToast(`${stmt.driverName} 様へ承認依頼（本人専用URL）を発行しました${mailed}`);
      } else {
        pushToast(`発行に失敗しました: ${r.error || "不明なエラー"}`, "warn");
      }
    } finally {
      setPublishBusy(false);
    }
  };

  // ===== 印刷（PDF） =====
  const [printMode, setPrintMode] = useState<"notice" | "sales">("notice");
  const [printTick, setPrintTick] = useState(0);
  useEffect(() => { if (printTick > 0) window.print(); }, [printTick]);
  const doPrint = (mode: "notice" | "sales") => { setPrintMode(mode); setPrintTick((t) => t + 1); };

  const printRoot = typeof document !== "undefined" ? document.getElementById("print-root") : null;

  return (
    <div className="content detail-content" style={{ maxWidth: 1320 }}>
      {/* アクションバー */}
      <div className="detail-actionbar">
        <button className="btn" onClick={goList}><Ic.chevronL size={13} /> 一覧に戻る</button>
        {hasNav && !correcting && (
          <div className="detail-nav">
            <button className="btn ghost" onClick={onPrev}><Ic.chevronL size={15} /> 前の人</button>
            <div className="detail-nav-pos">
              <div style={{ fontWeight: 800, fontSize: 13 }}>{stmt.driverName}</div>
              <div className="tiny muted num">
                {position} / {total} 名{navFilterLabel ? `（${navFilterLabel}）` : ""}
              </div>
            </div>
            <button className="btn ghost" onClick={onNext}>次の人 <Ic.chevronR size={15} /></button>
          </div>
        )}
        <div style={{ flex: 1 }} />
        {readOnly && !correcting && <span className="chip blue dot">過去月・読み取り専用</span>}
        {correcting && <span className="chip amber dot">この人だけ訂正中</span>}
        {canCorrect && !correcting && (
          <button className="btn" onClick={startCorrection} title="この月の他の方と、いま作業中の月には触れずに、この方の明細だけを直します">
            <Ic.edit size={14} /> この人だけ訂正
          </button>
        )}
        {!readOnly && locked && <span className="chip green dot" title="確定した月です。訂正するには支払明細一覧で「この月を訂正する」を押してください">確定済み・編集不可</span>}
        {isManual && !noEdit && (
          <button className="btn ghost" onClick={removeManual} style={{ color: "#e64646" }} title="手動作成した通知書を削除します">
            <Ic.trash size={13} /> 手動明細を削除
          </button>
        )}
        <button className="btn" onClick={() => doPrint("notice")}><Ic.download size={14} /> 支払通知書</button>
        <button className="btn" onClick={() => doPrint("sales")}><Ic.doc size={14} /> 売上明細表</button>
        {!readOnly && (
          <button className="btn primary" onClick={sendApproval} disabled={publishBusy} style={{ opacity: requestSent ? 0.75 : 1 }}>
            <Ic.send size={14} /> {publishBusy ? "発行中..." : requestSent ? "承認依頼済み" : "承認依頼を送る"}
          </button>
        )}
        {readOnly && !correcting && (justCorrected || corrections.length > 0) && (
          <button className={"btn" + (justCorrected ? " primary" : "")} onClick={sendApproval} disabled={publishBusy} title="訂正後の内容で、本人へ承認依頼を送り直します">
            <Ic.send size={14} /> {publishBusy ? "送信中..." : "承認依頼を送り直す"}
          </button>
        )}
      </div>

      {/* 過去月の1人訂正：状態と保存ボタン */}
      {correcting && draft && (
        <div className="toast warn" style={{ position: "static", marginBottom: 14, alignItems: "flex-start" }}>
          <Ic.edit size={16} />
          <div className="col gap-8" style={{ flex: 1, minWidth: 0 }}>
            <div>
              <b>{formatMonth(stmtMonth)}分の {stmtProp.driverName} 様だけを訂正しています。</b>
              <span className="tiny">「訂正を保存」を押すまで、保存済みの内容は変わりません。他の方の{formatMonth(stmtMonth)}分と、いま作業中の月には影響しません。</span>
            </div>
            <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <span className="num">差引支給額 ¥{yen(stmtProp.netTotal)} → <b>¥{yen(draft.netTotal)}</b>（{draft.netTotal - stmtProp.netTotal >= 0 ? "+" : "−"}{yen(Math.abs(draft.netTotal - stmtProp.netTotal))}）</span>
              <input className="input" placeholder="訂正の理由（任意。履歴に残ります）" value={correctReason} onChange={(e) => setCorrectReason(e.target.value)} style={{ flex: "1 1 220px", minWidth: 180 }} />
              <button className="btn primary" onClick={saveCorrection} disabled={archiveBusy || !draftChanged}><Ic.check size={14} /> {archiveBusy ? "保存中..." : "訂正を保存"}</button>
              <button className="btn ghost" onClick={cancelCorrection} disabled={archiveBusy}>やめる</button>
            </div>
            {unappliedInputs && <div className="tiny">「適用して保存」を押していない入力があります。押すと上の金額に反映されます。</div>}
          </div>
        </div>
      )}

      {/* 過去月の訂正履歴（この人の分） */}
      {readOnly && corrections.length > 0 && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>この方の訂正履歴（確定後）</div>
          {corrections.map((c, i) => (
            <div key={i} className="tiny" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <span>{new Date(c.at).toLocaleString("ja-JP")}</span>
              <span>{c.by}</span>
              <span className="num">差引支給額 ¥{yen(c.before.netTotal)} → ¥{yen(c.after.netTotal)}</span>
              {c.reason && <span className="muted">理由: {c.reason}</span>}
            </div>
          ))}
        </div>
      )}

      {/* 警告バナー */}
      {(warnCount > 0 || masterMissing || stmt.warnings.length > 0) && (
        <div className="detail-warn">
          <Ic.alert size={18} />
          {masterMissing
            ? <span>マスター未登録：単価マスターに「{stmt.driverName}（{stmt.org}）」が無く、単価が引けていない可能性があります。{warnCount > 0 ? `${warnCount}件の行が単価0です。` : ""}マスター登録後に再計算してください。</span>
            : warnCount > 0
            ? <span>単価0の行が {warnCount} 件あります。マスター単価を確認してください。</span>
            : <span>{stmt.warnings.join(" / ")}</span>}
        </div>
      )}

      {/* ドライバー情報カード */}
      <div className="card" style={{ padding: "18px 20px" }}>
        <div className="detail-name-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
            <div>
              <div className="detail-name" style={{ fontSize: 24, fontWeight: 800, letterSpacing: ".01em", lineHeight: 1.15 }}>
                {stmt.driverName}<span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-3)", marginLeft: 8 }}>様</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {carriers.map((c) => (
                <span key={c} className={`chip carrier-${c}`}>{CARRIER_LABEL[c]}</span>
              ))}
              {stmt.specialRule && <span className="badge-special">{stmt.specialRule}</span>}
              {masterMissing && <span className="chip red dot">マスター未登録</span>}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
            <StatusChip s={stmt.status} />
            <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600, whiteSpace: "nowrap" }}>対象月 <b style={{ color: "var(--ink)" }}>{formatMonth(stmt.targetMonth)}</b></div>
          </div>
        </div>

        <div style={{ height: 1, background: "var(--border)", margin: "16px 0 14px" }} />

        <div className="driver-fields">
          <Field label="管理番号" value={<span className="num">{stmt.driverId}</span>} />
          <Field label="所属" value={stmt.org || "—"} />
          <Field label="支店" value={stmt.branch || "—"} />
          <Field label="契約期間" value={driver ? `${driver.contractFrom || "—"} 〜 ${driver.contractTo || "現在"}` : "—"} />
          <Field label="インボイスT番号" value={
            stmt.invoiceNo
              ? <span className="num">{stmt.invoiceNo}</span>
              : <span className="chip amber" style={{ height: 22 }}><Ic.alert size={12} /> 未登録</span>
          } />
          <Field label="差引支給額（速報）" value={<span className="num" style={{ fontWeight: 800 }}>¥{yen(stmt.netTotal)}</span>} />
        </div>
      </div>

      {/* 2カラム */}
      <div className="detail-grid-2" style={{ marginTop: 18 }}>
        <div className="col gap-16" style={{ minWidth: 0 }}>
          {/* 売上明細 */}
          <div className="card" style={{ overflow: "hidden" }}>
            <DetailCardHead
              title="売上明細"
              right={
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="tiny muted">日別・便種別（数量0の日も表示）</span>
                  <button className="btn sm" onClick={() => doPrint("sales")}><Ic.doc size={14} /> 売上明細表(PDF)</button>
                </div>
              }
            />
            <div className="tbl-scroll" style={{ maxHeight: 430 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 92 }}>日付</th>
                    <th>便種</th>
                    <th className="num">実績</th>
                    <th className="num">単価(円)</th>
                    <th className="num">金額(円)</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyItems.map((r, i) => {
                    const prev = dailyItems[i - 1];
                    const showDate = !prev || prev.deliveryDate !== r.deliveryDate;
                    const zero = r.quantity === 0;
                    const warn = (r.unitPrice ?? 0) === 0 && r.quantity > 0;
                    return (
                      <tr key={i} className={warn ? "warn-row" : ""} style={zero ? { color: "var(--ink-4)" } : undefined}>
                        <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{showDate ? r.deliveryDate.slice(5).replace("-", "/") : ""}</td>
                        <td>
                          {isMultiCarrier && r.carrier && <span className={`chip carrier-${r.carrier}`} style={{ marginRight: 6 }}>{CARRIER_LABEL[r.carrier]}</span>}
                          {r.name}
                          {r.detail && <div className="tiny muted" style={{ marginTop: 2 }}>{r.detail}</div>}
                        </td>
                        <td className="num">{r.quantity.toLocaleString()} 個</td>
                        <td className="num">
                          {yen(r.unitPrice ?? 0)}
                          {warn && <div style={{ marginTop: 3 }}><span className="chip red" style={{ height: 20 }}>単価0</span></div>}
                        </td>
                        <td className="num" style={{ fontWeight: zero ? 400 : 700 }}>{yen(r.amount)}</td>
                      </tr>
                    );
                  })}
                  <tr className="total-row">
                    <td colSpan={4}><b>売上合計</b></td>
                    <td className="num"><b>{yen(stmt.salesTotal)}</b></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 控除明細 */}
          <div className="card" style={{ overflow: "hidden" }}>
            <DetailCardHead
              title="控除明細"
              right={!canEditAmounts ? undefined : <button className="btn sm" onClick={() => setEditOpen((v) => !v)}><Ic.edit size={14} /> {editOpen ? "編集を閉じる" : "控除・調整を編集"}</button>}
            />
            <table className="tbl">
              <thead><tr><th>項目</th><th>備考</th><th className="num">金額(△円)</th></tr></thead>
              <tbody>
                {deductItems.map((it, i) => (
                  <tr key={`d${i}`}>
                    <td style={{ fontWeight: 600 }}>{it.name}</td>
                    <td className="tiny muted">
                      {it.note === INVOICE_DISCOUNT_NOTE
                        ? "インボイス未登録（(課税売上−課税控除)×消費税の経過措置分）"
                        : it.note || (it.taxRate === "non" ? "不課税" : `税率 ${it.taxRate}`)}
                    </td>
                    <td className="num neg">△{yen(Math.abs(it.amount))}</td>
                  </tr>
                ))}
                {manualItems.map((it, i) => (
                  <tr key={`m${i}`}>
                    <td style={{ fontWeight: 600 }}>{it.name}</td>
                    <td className="tiny muted">{it.unitPrice == null ? "未入力（手入力控除で設定）" : it.note || "手入力"}</td>
                    <td className="num neg">{it.unitPrice == null ? <span className="muted">—</span> : <>△{yen(Math.abs(it.amount))}</>}</td>
                  </tr>
                ))}
                {savedExtras.map((e) => (
                  <tr key={`e${e.id}`}>
                    <td style={{ fontWeight: 600 }}>{e.name} <span className="chip blue" style={{ height: 20, marginLeft: 6 }}>臨時</span></td>
                    <td className="tiny muted">
                      {e.detail && <span style={{ color: "#1b2536" }}>{e.detail}　</span>}
                      {(e.taxRate ?? "non") === "10%" ? "課税 10%" : (e.taxRate === "8%" ? "軽減 8%" : "不課税（立替経費等）")}
                    </td>
                    <td className="num neg">△{yen(e.amount)}</td>
                  </tr>
                ))}
                {deductItems.length === 0 && manualItems.length === 0 && savedExtras.length === 0 && (
                  <tr><td colSpan={3} className="muted" style={{ textAlign: "center", padding: 16 }}>控除はありません</td></tr>
                )}
                <tr className="total-row">
                  <td colSpan={2}><b>控除合計</b></td>
                  <td className="num neg"><b>△{yen(stmt.deductTotal)}</b></td>
                </tr>
              </tbody>
            </table>

            {/* 控除編集（折りたたみ） */}
            {editOpen && canEditAmounts && (
              <div className="deduct-editor card-pad col gap-16" style={{ borderTop: "1px solid var(--border)", background: "var(--surface-2)" }}>
                {correcting && regularDeducts.length > 0 && (
                  <div className="col gap-8">
                    <div style={{ fontWeight: 700, fontSize: 12.5 }}>通常の控除（携帯・車両リース料など）の金額</div>
                    <div className="tiny muted">確定したときの金額を直します。0 にするとその控除行を消します（税抜・円）。</div>
                    {regularDeducts.map(({ it, i }) => (
                      <div key={`r${i}`} className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ minWidth: 170, fontWeight: 600 }}>{it.name}</span>
                        <input className="input" type="number" min={0} step={1} value={regularValue(i, it)} onChange={(e) => setRegularInputs((m) => ({ ...m, [i]: e.target.value }))} style={{ width: 140, textAlign: "right" }} />
                        <span className="muted tiny">円（{it.taxRate === "non" ? "不課税" : `税率 ${it.taxRate}`}）</span>
                      </div>
                    ))}
                    <div className="row gap-8">
                      <div style={{ flex: 1 }} />
                      <button className="btn primary" onClick={applyRegular} disabled={!hasUnsavedRegular}><Ic.check size={14} /> 適用して保存</button>
                    </div>
                  </div>
                )}
                {(hasGasolineSlot || shidoSlotCount > 0) && (
                  <div className="col gap-8">
                    <div style={{ fontWeight: 700, fontSize: 12.5 }}>手入力控除（ガソリン代・指導料）</div>
                    <div className="manual-grid">
                      {hasGasolineSlot && (
                        <div className="manual-field">
                          <label className="muted tiny">ガソリンカード利用料（円）</label>
                          <input className="input" type="number" min={0} step={1} placeholder="例: 28000" value={gasolineInput} onChange={(e) => setGasolineInput(e.target.value)} />
                        </div>
                      )}
                      {shidoSlotCount > 0 && (
                        <div className="manual-field">
                          <label className="muted tiny">指導料 単価（円）× {shidoSlotCount} 件</label>
                          <input className="input" type="number" min={0} step={1} placeholder="例: 5500" value={shidoInput} onChange={(e) => setShidoInput(e.target.value)} />
                          <div className="tiny muted">計 {yen(Number(shidoInput || 0) * shidoSlotCount)} 円</div>
                        </div>
                      )}
                    </div>
                    <div className="row gap-8">
                      <button className="btn primary" onClick={applyOverrides} disabled={!hasUnsavedManualEdit}><Ic.check size={14} /> 適用して保存</button>
                      {(overrides.gasoline != null || overrides.shido != null) && (
                        <button className="btn ghost" onClick={() => { setGasolineInput(""); setShidoInput(""); editStatement((s) => applyManualOverrides(s, {}), { type: "SET_MANUAL_OVERRIDES", driverId: stmt.driverId, overrides: {} }); }}>クリア</button>
                      )}
                    </div>
                  </div>
                )}

                <div className="col gap-8">
                  <div style={{ fontWeight: 700, fontSize: 12.5 }}>臨時控除（その月だけの控除・課税/不課税を選択）</div>
                  {extras.length === 0 && <div className="tiny muted" style={{ fontStyle: "italic" }}>「+ 控除を追加」で1行ずつ追加してください。立替金は通常「不課税」です。</div>}
                  {extras.map((r) => (
                    <div key={r.id} className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                      <select className="select" value={r.preset} onChange={(e) => onPresetChange(r.id, e.target.value)} style={{ minWidth: 130 }}>
                        {presetOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      {r.preset === OTHER_OPTION && (
                        <input className="input" placeholder="控除名を入力" value={r.customName} onChange={(e) => updateExtra(r.id, { customName: e.target.value })} style={{ flex: "1 1 160px", minWidth: 140 }} />
                      )}
                      <input className="input" placeholder="備考（任意。通知書に表示）" value={r.detail} onChange={(e) => updateExtra(r.id, { detail: e.target.value })} style={{ flex: "1 1 180px", minWidth: 160 }} title="控除名の下に小さく表示されます（例: 8/12 駐車場代）。ドライバーの承認ページにも出ます" />
                      <input className="input" type="number" min={0} step={1} placeholder="金額（円）" value={r.amount} onChange={(e) => updateExtra(r.id, { amount: e.target.value })} style={{ width: 140, textAlign: "right" }} />
                      <span className="muted tiny">円</span>
                      <select className="select" value={r.taxRate} onChange={(e) => updateExtra(r.id, { taxRate: e.target.value as TaxRate })} style={{ width: 112 }} title="課税にすると消費税・インボイス値引きの計算に含まれます。8%は軽減税率（通知書に ※ と注記が付きます）">
                        <option value="non">不課税</option>
                        <option value="10%">課税10%</option>
                        <option value="8%">軽減8%</option>
                      </select>
                      <div style={{ flex: 1 }} />
                      <button className="btn sm ghost" onClick={() => removeExtra(r.id)} title="この行を削除" style={{ color: "var(--red)" }}><Ic.trash size={13} /></button>
                    </div>
                  ))}
                  <div className="row gap-8">
                    <button className="btn" onClick={addExtra}><Ic.plus size={13} /> 控除を追加</button>
                    <div style={{ flex: 1 }} />
                    <button className="btn primary" onClick={applyExtras} disabled={!hasUnsavedExtras}><Ic.check size={14} /> 適用して保存</button>
                  </div>
                </div>

                <div className="col gap-8">
                  <div style={{ fontWeight: 700, fontSize: 12.5 }}>調整・臨時売上（加算。橋本様の全日/半日・代走調整など）</div>
                  <div className="row gap-6" style={{ flexWrap: "wrap" }}>
                    {ADJ_PRESETS.map((p) => (
                      <button key={p.name} className="btn sm ghost" onClick={() => addAdj(p)} title={p.unit != null ? `単価 ${p.unit} 円` : "単価は手入力"}>
                        ＋ {p.name}
                      </button>
                    ))}
                  </div>
                  {adjRows.length === 0 && <div className="tiny muted" style={{ fontStyle: "italic" }}>上のボタンから行を追加してください（例: 全日 12,000円 × 13日）。売上側に加算され、消費税・値引きにも反映されます。</div>}
                  {adjRows.map((r) => (
                    <div key={r.id} className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                      <input className="input" placeholder="名称（例: 全日）" value={r.name} onChange={(e) => updateAdj(r.id, { name: e.target.value })} style={{ width: 150 }} />
                      <input className="input" placeholder="品目詳細（任意）" value={r.detail} onChange={(e) => updateAdj(r.id, { detail: e.target.value })} style={{ width: 170 }} />
                      <input className="input" type="number" step={0.01} placeholder="単価（円）" value={r.unitPrice} onChange={(e) => updateAdj(r.id, { unitPrice: e.target.value })} style={{ width: 120, textAlign: "right" }} />
                      <span className="muted tiny">×</span>
                      <input className="input" type="number" step={0.5} placeholder="数量" value={r.quantity} onChange={(e) => updateAdj(r.id, { quantity: e.target.value })} style={{ width: 90, textAlign: "right" }} />
                      <select className="select" value={r.taxRate} onChange={(e) => updateAdj(r.id, { taxRate: e.target.value as TaxRate })} style={{ width: 112 }} title="8%は軽減税率（通知書に ※ と注記が付きます）">
                        <option value="10%">課税10%</option>
                        <option value="8%">軽減8%</option>
                        <option value="non">不課税</option>
                      </select>
                      <span className="tiny num muted">= {yen(Math.floor((Math.round(Number(r.unitPrice || 0) * 100) * Number(r.quantity || 0)) / 100))} 円</span>
                      <div style={{ flex: 1 }} />
                      <button className="btn sm ghost" onClick={() => removeAdj(r.id)} title="この行を削除" style={{ color: "var(--red)" }}><Ic.trash size={13} /></button>
                    </div>
                  ))}
                  <div className="row gap-8">
                    <button className="btn" onClick={() => addAdj()}><Ic.plus size={13} /> 調整行を追加</button>
                    <div style={{ flex: 1 }} />
                    <button className="btn primary" onClick={applyAdj} disabled={!hasUnsavedAdj}><Ic.check size={14} /> 適用して保存</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 差引支給額カード */}
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="netcard">
              <div className="netcard-main">
                <div style={{ fontSize: 13, color: "#aeb8cf", fontWeight: 700, letterSpacing: ".04em" }}>
                  差引支給額{stmt.taxSummary ? "（税込）" : "（売上合計 − 控除合計）"}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginTop: 6 }}>
                  <span style={{ fontSize: 26, fontWeight: 700 }}>¥</span>
                  <span className="num" style={{ fontSize: 46, fontWeight: 800, lineHeight: 1, letterSpacing: "-.01em" }}>{yen(stmt.netTotal)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: "#aeb8cf", marginTop: 10, display: "flex", gap: 18, flexWrap: "wrap" }}>
                  <span>売上 <b className="num" style={{ color: "#fff" }}>¥{yen(stmt.salesTotal)}</b></span>
                  <span>控除 <b className="num" style={{ color: "#ffb3ad" }}>△{yen(stmt.deductTotal)}</b></span>
                  {stmt.taxSummary && (
                    <span>消費税(10%) <b className="num" style={{ color: "#fff" }}>¥{yen(stmt.taxSummary.tax10)}</b></span>
                  )}
                  <span>振込予定 <b style={{ color: "#fff" }}>{stmt.dueDate || "—"}</b></span>
                </div>
              </div>
              {!noEdit && (
                <div className="netcard-actions">
                  <button
                    className="btn primary"
                    style={{ height: 44, fontSize: 14.5, ...(stmt.status === "confirmed" ? { background: "var(--green)", borderColor: "var(--green)" } : {}) }}
                    onClick={() => setStatus("confirmed", "確定")}
                    disabled={stmt.status === "confirmed"}
                  >
                    <Ic.check size={17} /> {stmt.status === "confirmed" ? "確定済み" : "この明細を確定"}
                  </button>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn" style={{ flex: 1 }} onClick={() => setStatus("hold", "保留")} disabled={stmt.status === "hold"}>
                      {stmt.status === "hold" ? "保留中" : "保留"}
                    </button>
                    <button className="btn danger" style={{ flex: 1 }} onClick={() => setStatus("draft", "差戻し")} disabled={stmt.status === "draft"}>差戻し</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 右カラム：支払通知書プレビュー */}
        <div style={{ minWidth: 0 }}>
          <div className="card" style={{ overflow: "hidden", position: "sticky", top: 72 }}>
            <DetailCardHead
              title="支払通知書 プレビュー"
              icon="doc"
              right={<button className="btn sm primary" onClick={() => doPrint("notice")}><Ic.download size={14} /> PDF出力</button>}
            />
            <div className="scroll" style={{ maxHeight: 760, overflow: "auto", background: "#e9ecf1", padding: 18 }}>
              <div className="a4-sheet">
                <NoticeBody stmt={stmt} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 確定の記録 */}
      <div style={{ marginTop: 18 }}>
        <div className="card" style={{ overflow: "hidden" }}>
          <DetailCardHead title="確定の記録" icon="clock" right={<span className="tiny muted">確定・承認の記録のみを保持します</span>} />
          <div style={{ padding: "16px 20px", display: "flex", gap: 14, flexWrap: "wrap" }}>
            <RecordItem label="確定" done={!!stmt.confirmedAt} time={stmt.confirmedAt} who={stmt.confirmedBy} todo="未確定" />
            <RecordItem label="ドライバー承認" done={!!approvedAt} time={approvedAt} who={`${stmt.driverName} 様`} todo={requestSent ? "承認待ち" : "未発行"} />
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 22, fontSize: 12 }}>
        <a onClick={goList} style={{ color: "var(--blue)", fontWeight: 700, cursor: "pointer" }}>← 支払明細 一覧に戻る</a>
      </div>

      {/* トースト */}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div key={t.id} className="toast-pop">
            <span style={{ color: t.kind === "ok" ? "#79e3a6" : "#ffd479" }}>{t.kind === "ok" ? <Ic.check size={17} /> : <Ic.alert size={17} />}</span>
            {t.text}
          </div>
        ))}
      </div>

      {/* 印刷ポータル */}
      {printRoot && createPortal(
        printMode === "sales" ? <SalesPrintSheet stmt={stmt} dailyItems={dailyItems} /> : <NoticePrintSheet stmt={stmt} />,
        printRoot
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: "var(--ink-3)", fontWeight: 700, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function DetailCardHead({ title, right, icon }: { title: string; right?: React.ReactNode; icon?: keyof typeof Ic }) {
  const Icon = icon ? Ic[icon] : null;
  return (
    <div className="detail-card-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 18px", borderBottom: "1px solid var(--border)" }}>
      <div className="detail-card-head-title" style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14.5 }}>
        {Icon && <span style={{ color: "var(--ink-3)" }}><Icon size={17} /></span>}{title}
      </div>
      {right}
    </div>
  );
}

function RecordItem({ label, done, time, who, todo }: { label: string; done: boolean; time?: string | null; who?: string | null; todo: string }) {
  return (
    <div className="record-item" style={{ borderColor: done ? "var(--green-border)" : "var(--border)", background: done ? "var(--green-soft)" : "var(--surface-2)" }}>
      <div className="record-icon" style={{ background: done ? "var(--green)" : "#fff", borderColor: done ? "var(--green)" : "var(--border-strong)", color: done ? "#fff" : "var(--ink-4)" }}>
        {done ? <Ic.check size={19} /> : <Ic.clock size={19} />}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>{label}{done ? "済み" : ""}</div>
        {done
          ? <div style={{ fontSize: 11.5, color: "var(--ink-2)", fontWeight: 600 }}><span className="num">{time}</span> ・ {who}</div>
          : <div style={{ fontSize: 11.5, color: "var(--ink-4)", fontWeight: 600 }}>{todo}</div>}
      </div>
    </div>
  );
}

function StatusChip({ s }: { s: Statement["status"] }) {
  if (s === "confirmed") return <span className="chip green dot">確定</span>;
  if (s === "hold") return <span className="chip amber dot">保留</span>;
  if (s === "error") return <span className="chip red dot">要確認</span>;
  return <span className="chip dot">下書き</span>;
}

// ===== 支払通知書本体（プレビューと印刷で共用） =====
function NoticeBody({ stmt }: { stmt: Statement }) {
  const sales = summarizeSales(stmt);
  // 軽減税率（8%）対象の行があるか。適格請求書は「軽減税率の対象品目である旨」の記載が要件のため、
  // 対象行に ※ を付け、欄外に注記を出す（本城さん要望 2026-08-07）。
  const hasReduced = stmt.items.some((it) => it.taxRate === "8%");
  const mark = (taxRate?: string) => (taxRate === "8%" ? " ※" : "");
  const deducts = stmt.items.filter((i) => (i.category === "deduct" && i.note !== "__extra__") || (i.category === "manual" && i.unitPrice != null));
  const extras = stmt.extraDeductions || [];
  const pdfTh: React.CSSProperties = { padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #cdd3dc", fontWeight: 700, fontSize: 10 };
  const pdfTd: React.CSSProperties = { padding: "4px 8px", borderBottom: "1px solid #eef1f5" };
  return (
    <>
      <div style={{ textAlign: "center", fontSize: 17, fontWeight: 800, letterSpacing: ".3em", borderBottom: "2px solid var(--navy)", paddingBottom: 8 }}>支　払　通　知　書</div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, alignItems: "flex-start" }}>
        <div>
          {(() => {
            const rcp = noticeRecipient(stmt.org, stmt.driverName);
            return (
              <>
                <div style={{ fontSize: 15, fontWeight: 800, borderBottom: "1px solid #1b2536", paddingBottom: 2, display: "inline-block" }}>{rcp.name}　{rcp.honorific}</div>
                {/* 受取側（ドライバー様）のインボイス登録番号。MF請求書と同じ位置＝宛名の直下に出す
                    （本城さん要望 2026-08-07）。未登録の方は行ごと出さない（備考に値引きの説明が入る）。 */}
                {displayInvoiceNo(stmt.invoiceNo) && (
                  <div style={{ marginTop: 8, color: "#4d5765" }}>登録番号：{displayInvoiceNo(stmt.invoiceNo)}</div>
                )}
                <div style={{ marginTop: displayInvoiceNo(stmt.invoiceNo) ? 0 : 8, color: "#4d5765" }}>管理番号：{stmt.driverId}</div>
                {rcp.usedOrg && <div style={{ color: "#4d5765" }}>氏名：{stmt.driverName}　様</div>}
                <div style={{ color: "#4d5765" }}>対象月：{formatMonth(stmt.targetMonth)}</div>
              </>
            );
          })()}
        </div>
        <div style={{ textAlign: "right" }}>
          <img src={logo} alt="HS-deliverys" style={{ height: 17, marginBottom: 4 }} />
          <div style={{ fontWeight: 800 }}>{COMPANY.name}</div>
          <div style={{ color: "#4d5765" }}>{COMPANY.zip} {COMPANY.addr}</div>
          <div style={{ color: "#4d5765" }}>TEL {COMPANY.tel}</div>
          <div style={{ marginTop: 3, fontWeight: 700 }} className="num">登録番号 {COMPANY.invoiceNo}</div>
        </div>
      </div>

      <div style={{ marginTop: 16, fontWeight: 800, fontSize: 12, borderLeft: "3px solid var(--blue)", paddingLeft: 7 }}>売上明細</div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 10.5 }}>
        <thead><tr style={{ background: "#f4f6f9" }}>
          <th style={pdfTh}>区分</th><th style={{ ...pdfTh, textAlign: "right" }}>実績</th><th style={{ ...pdfTh, textAlign: "right" }}>金額</th>
        </tr></thead>
        <tbody>
          {sales.map((s, i) => (
            <tr key={i}>
              <td style={pdfTd}>
                {s.carrier ? `[${CARRIER_LABEL[s.carrier]}] ` : ""}{s.name}{mark(s.taxRate)}
                {s.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{s.detail}</div>}
              </td>
              <td style={{ ...pdfTd, textAlign: "right" }} className="num">{s.qty.toLocaleString()} 個</td>
              <td style={{ ...pdfTd, textAlign: "right" }} className="num">{yen(s.amount)}</td>
            </tr>
          ))}
          <tr style={{ background: "#f4f6f9", fontWeight: 800 }}><td style={pdfTd} colSpan={2}>売上合計</td><td style={{ ...pdfTd, textAlign: "right" }} className="num">¥{yen(stmt.salesTotal)}</td></tr>
        </tbody>
      </table>

      <div style={{ marginTop: 14, fontWeight: 800, fontSize: 12, borderLeft: "3px solid var(--red)", paddingLeft: 7 }}>控除明細</div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 10.5 }}>
        <tbody>
          {deducts.map((x, i) => (
            <tr key={`d${i}`}><td style={pdfTd}>{x.name}{mark(x.taxRate)}</td><td style={{ ...pdfTd, textAlign: "right", color: "var(--red)" }} className="num">△{yen(Math.abs(x.amount))}</td></tr>
          ))}
          {extras.map((e) => (
            <tr key={`e${e.id}`}>
              <td style={pdfTd}>
                {e.name}{mark(e.taxRate ?? "non")}
                {e.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{e.detail}</div>}
              </td>
              <td style={{ ...pdfTd, textAlign: "right", color: "var(--red)" }} className="num">△{yen(e.amount)}</td>
            </tr>
          ))}
          <tr style={{ background: "#f4f6f9", fontWeight: 800 }}><td style={pdfTd}>控除合計</td><td style={{ ...pdfTd, textAlign: "right", color: "var(--red)" }} className="num">△{yen(stmt.deductTotal)}</td></tr>
        </tbody>
      </table>

      {/* 税集計（MF通知書形式：税率別内訳 + 小計/消費税額合計/合計。本城さん指示 6/19） */}
      {stmt.taxSummary ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 14, gap: 16 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 9.5 }}>
              <thead>
                <tr>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "left", color: "#4d5765" }}>税率別内訳</th>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "right", color: "#4d5765" }}>税抜金額</th>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "right", color: "#4d5765" }}>消費税額</th>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "right", color: "#4d5765" }}>税込金額</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 8px" }}>10%</td>
                  <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.taxable10)}</td>
                  <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.tax10)}</td>
                  <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.taxable10 + stmt.taxSummary.tax10)}</td>
                </tr>
                {(stmt.taxSummary.taxable8 ?? 0) !== 0 && (
                  <tr>
                    <td style={{ padding: "3px 8px" }}>8%（軽減）</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.taxable8 ?? 0)}</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.tax8 ?? 0)}</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen((stmt.taxSummary.taxable8 ?? 0) + (stmt.taxSummary.tax8 ?? 0))}</td>
                  </tr>
                )}
                {stmt.taxSummary.nonTaxable !== 0 && (
                  <tr>
                    <td style={{ padding: "3px 8px" }}>不課税</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.nonTaxable)}</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>—</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{yen(stmt.taxSummary.nonTaxable)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <table style={{ borderCollapse: "collapse", fontSize: 10.5, minWidth: 220 }}>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 10px", color: "#4d5765" }}>小計</td>
                  <td className="num" style={{ padding: "3px 0", textAlign: "right", fontWeight: 700 }}>{yen(stmt.taxSummary.subtotal)}</td>
                </tr>
                <tr>
                  <td style={{ padding: "3px 10px", color: "#4d5765" }}>消費税額合計</td>
                  <td className="num" style={{ padding: "3px 0", textAlign: "right", fontWeight: 700 }}>{yen(stmt.taxSummary.tax10 + (stmt.taxSummary.tax8 ?? 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10, background: "var(--navy)", color: "#fff", borderRadius: 6, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, letterSpacing: ".06em" }}>差引支給額（税込）</span>
            <span className="num" style={{ fontSize: 22, fontWeight: 800 }}>¥{yen(stmt.netTotal)}</span>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 16, background: "var(--navy)", color: "#fff", borderRadius: 6, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, letterSpacing: ".06em" }}>差引支給額</span>
          <span className="num" style={{ fontSize: 22, fontWeight: 800 }}>¥{yen(stmt.netTotal)}</span>
        </div>
      )}

      <div style={{ marginTop: 12, color: "#4d5765" }}>振込予定日：{stmt.dueDate || "—"}</div>
      {/* 軽減税率の対象品目である旨（適格請求書の記載要件）。対象行が無いときは出さない */}
      {hasReduced && (
        <div style={{ marginTop: 10, fontSize: 10.5, fontWeight: 700 }}>※は軽減税率（8％）対象</div>
      )}
      <div style={{ marginTop: 12, borderTop: "1px solid #e2e6ec", paddingTop: 8, fontSize: 10, color: "#828b99" }}>
        備考：本通知書は適格請求書等保存方式（インボイス制度）に対応しています。{!stmt.invoiceNo && "インボイス未登録のため、消費税相当の一部（値引き）を調整しています。"}
      </div>
    </>
  );
}

function NoticePrintSheet({ stmt }: { stmt: Statement }) {
  return (
    <div style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#1b2536", fontSize: 11, lineHeight: 1.5 }}>
      <NoticeBody stmt={stmt} />
    </div>
  );
}

// 売上明細表の行（納品日の昇順）。詳細画面と一括出力で同じ導出を使う。
export function sortedSalesItems(stmt: Statement): ItemRow[] {
  return stmt.items
    .filter((i) => i.category === "sales")
    .slice()
    .sort((a, b) => (a.deliveryDate < b.deliveryDate ? -1 : a.deliveryDate > b.deliveryDate ? 1 : 0));
}

// ===== 一括PDF出力（本城さん要望 2026-08-06）=====
// 一覧画面から複数名ぶんの帳票を1回の印刷にまとめて流し込む。
// ブラウザの印刷ダイアログで「PDFに保存」を選ぶと、全員分が1つのPDF（1人1ページ〜）になる。
// 改ページは app.css の `.print-page + .print-page { break-before: page }` が担う。
export function BulkPrintSheets({ statements, mode }: { statements: Statement[]; mode: "notice" | "sales" }) {
  return (
    <>
      {statements.map((s) => (
        <div className="print-page" key={s.driverId}>
          {mode === "sales" ? (
            <SalesPrintSheet stmt={s} dailyItems={sortedSalesItems(s)} />
          ) : (
            <NoticePrintSheet stmt={s} />
          )}
        </div>
      ))}
    </>
  );
}

// 帳票のロゴ画像を先に読み込んでおく。
// 未ロードのまま window.print() すると、1ページ目以外のロゴが欠けたPDFになるため。
export function preloadPrintAssets(): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = logo;
  });
}

// ===== 売上明細表（印刷・A4・日別/便種別 全行） =====
function SalesPrintSheet({ stmt, dailyItems }: { stmt: Statement; dailyItems: ItemRow[] }) {
  const th: React.CSSProperties = { padding: "5px 8px", fontSize: 10.5, fontWeight: 700, borderBottom: "1.5px solid #1b2536", textAlign: "left" };
  const thr: React.CSSProperties = { ...th, textAlign: "right" };
  const td: React.CSSProperties = { padding: "4px 8px", fontSize: 10.5, borderBottom: "1px solid #e8ebf0" };
  const tdr: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  return (
    <div style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#1b2536" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1b2536", paddingBottom: 8 }}>
        <div>
          <img src={logo} alt="HS-deliverys" style={{ height: 18, display: "block", marginBottom: 6 }} />
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: ".08em" }}>売上明細表</div>
          <div style={{ fontSize: 11, color: "#4d5765", marginTop: 4 }}>対象月：{formatMonth(stmt.targetMonth)}</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 10.5, color: "#4d5765" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#1b2536" }}>{COMPANY.name}</div>
          <div>{COMPANY.zip} {COMPANY.addr}</div>
          <div>登録番号 {COMPANY.invoiceNo}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 24, margin: "10px 0 12px", fontSize: 11 }}>
        <div><span style={{ color: "#828b99" }}>氏名</span>　<b style={{ fontSize: 13 }}>{stmt.driverName}　様</b></div>
        <div><span style={{ color: "#828b99" }}>管理番号</span>　{stmt.driverId}</div>
        <div><span style={{ color: "#828b99" }}>所属</span>　{stmt.org}</div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={{ ...th, width: 70 }}>日付</th><th style={th}>便種</th><th style={thr}>実績(個)</th><th style={thr}>単価(円)</th><th style={thr}>金額(円)</th>
        </tr></thead>
        <tbody>
          {dailyItems.map((r, i) => {
            const prev = dailyItems[i - 1];
            const showDate = !prev || prev.deliveryDate !== r.deliveryDate;
            const zero = r.quantity === 0;
            const color = zero ? "#a7aebb" : "#1b2536";
            return (
              <tr key={i} style={showDate ? { borderTop: "1px solid #cdd3dc" } : undefined}>
                <td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{showDate ? r.deliveryDate.slice(5).replace("-", "/") : ""}</td>
                <td style={{ ...td, color }}>
                  {r.carrier ? `[${CARRIER_LABEL[r.carrier]}] ` : ""}{r.name}
                  {r.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{r.detail}</div>}
                </td>
                <td style={{ ...tdr, color }}>{r.quantity.toLocaleString()} 個</td>
                <td style={{ ...tdr, color }}>{yen(r.unitPrice ?? 0)}</td>
                <td style={{ ...tdr, fontWeight: zero ? 400 : 700, color }}>{yen(r.amount)}</td>
              </tr>
            );
          })}
          <tr><td style={{ ...td, borderTop: "1.5px solid #1b2536", borderBottom: "none", fontWeight: 800 }} colSpan={4}>売上合計</td><td style={{ ...tdr, borderTop: "1.5px solid #1b2536", borderBottom: "none", fontWeight: 800, fontSize: 13 }}>¥{yen(stmt.salesTotal)}</td></tr>
        </tbody>
      </table>
      <div style={{ marginTop: 14, fontSize: 9.5, color: "#828b99", lineHeight: 1.6 }}>
        ※ 本表は売上（配達実績）の明細です。控除・差引支給額は「支払通知書」をご確認ください。数量0の日も記録として表示しています。<br />
        発行：{COMPANY.name}　／　適格請求書等保存方式（インボイス制度）対応
      </div>
    </div>
  );
}
